import { useCallback, useEffect, useMemo, useState } from "react";
import { useCondos } from "context/CondosContext";
import { useAuth } from "context/AuthContext";
import { Select } from "components/ui/Select";
import Spinner from "components/ui/Spinner";
import { KnowledgeMarkdown } from "components/KnowledgeMarkdown";
import fetchWithCredentials from "apiClient";
import {
  KnowledgeItem,
  KnowledgeResponse,
  KnowledgeScope,
} from "types/Knowledge";
import { MOBILE_PAGE_TITLE_MENU_INSET } from "constants/pageLayout";
import { cn } from "lib/utils";

type Locale = "es" | "en";

function scopeLabel(
  scope: KnowledgeScope,
  tr: (es: string, en: string) => string,
): string {
  switch (scope) {
    case "unit":
      return tr("Unidad", "Unit");
    case "condo":
      return tr("Edificio", "Building");
    case "property_manager":
      return tr("Empresa", "Company");
    case "topic":
      return tr("Tema", "Topic");
    case "condo_topic":
    case "topic_condo":
      return tr("Edificio + tema", "Building + topic");
    default:
      return "";
  }
}

function KnowledgeDataGrid({
  items,
  emptyLabel,
  loading,
  error,
  tr,
}: {
  items: KnowledgeItem[];
  emptyLabel: string;
  loading: boolean;
  error: string | null;
  tr: (es: string, en: string) => string;
}) {
  if (loading) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4">
        <p className="text-sm text-red-600">{error}</p>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-4">
        <p className="text-sm text-gray-500">{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 bg-white">
      <div className="h-full overflow-y-auto overscroll-y-contain">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 border-b border-gray-200 bg-gray-100 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
            <tr>
              <th className="w-40 px-3 py-2.5 lg:w-48">
                {tr("Ámbito", "Scope")}
              </th>
              <th className="px-3 py-2.5">{tr("Contenido", "Content")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => (
              <tr
                key={`${item.scope}-${index}-${item.text.slice(0, 32)}`}
                className={cn(
                  "border-b border-gray-100 align-top",
                  item.matched
                    ? "bg-amber-50"
                    : index % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50/80",
                )}
              >
                <td className="px-3 py-2.5">
                  <div className="flex flex-col gap-1">
                    <span
                      className={cn(
                        "font-medium",
                        item.matched ? "text-amber-900" : "text-gray-500",
                      )}
                    >
                      {scopeLabel(item.scope, tr)}
                    </span>
                    <span
                      className={cn(
                        "text-xs",
                        item.matched ? "text-amber-700" : "text-gray-400",
                      )}
                    >
                      {item.matched
                        ? tr("Nivel actual", "Current level")
                        : tr("Heredado", "Inherited")}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2.5 align-top">
                  <KnowledgeMarkdown
                    content={item.text}
                    className={item.matched ? "text-gray-900" : "text-gray-600"}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Knowledge() {
  const { condos } = useCondos();
  const { lang } = useAuth();
  const locale: Locale = lang === "es" ? "es" : "en";
  const tr = useCallback(
    (es: string, en: string) => (locale === "es" ? es : en),
    [locale],
  );

  const [condoFilter, setCondoFilter] = useState("");
  const [unitFilter, setUnitFilter] = useState("");
  const [topicFilter, setTopicFilter] = useState("");
  const [knowledge, setKnowledge] = useState<KnowledgeItem[]>([]);
  const [filterOptions, setFilterOptions] = useState<
    KnowledgeResponse["filterOptions"]
  >({ units: [], topics: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const condoOptions = useMemo(
    () => [
      {
        value: "",
        label: tr("General (empresa)", "General (company)"),
      },
      ...condos.map((condo) => ({
        value: String(condo.id),
        label: condo.name,
      })),
    ],
    [condos, tr],
  );

  const unitOptions = useMemo(() => {
    const opts = [
      {
        value: "",
        label: tr("General (edificio)", "General (building)"),
      },
    ];
    if (filterOptions?.units) {
      for (const unit of filterOptions.units) {
        opts.push({ value: String(unit.id), label: unit.name });
      }
    }
    return opts;
  }, [filterOptions, tr]);

  const topicOptions = useMemo(() => {
    const opts = [
      {
        value: "",
        label: tr("General (sin tema)", "General (no topic)"),
      },
    ];
    if (filterOptions?.topics) {
      for (const topic of filterOptions.topics) {
        opts.push({ value: String(topic.id), label: topic.name });
      }
    }
    return opts;
  }, [filterOptions, tr]);

  const handleCondoChange = (value: string) => {
    setCondoFilter(value);
    setUnitFilter("");
    setTopicFilter("");
  };

  useEffect(() => {
    async function fetchKnowledge() {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (condoFilter) params.set("condo", condoFilter);
        if (unitFilter) params.set("unit", unitFilter);
        if (topicFilter) params.set("topic", topicFilter);

        const url = `/manager-api/knowledge/?${params.toString()}`;
        const data: KnowledgeResponse = await fetchWithCredentials(url);
        setKnowledge(data.knowledge ?? []);
        setFilterOptions(data.filterOptions ?? { units: [], topics: [] });

        if (
          unitFilter &&
          !data.filterOptions?.units?.some((u) => String(u.id) === unitFilter)
        ) {
          setUnitFilter("");
        }
        if (
          topicFilter &&
          !data.filterOptions?.topics?.some((t) => String(t.id) === topicFilter)
        ) {
          setTopicFilter("");
        }
      } catch (err: unknown) {
        console.error(err);
        setError(
          err instanceof Error
            ? err.message
            : tr("Error al cargar el conocimiento", "Failed to load knowledge"),
        );
      } finally {
        setLoading(false);
      }
    }

    fetchKnowledge();
  }, [condoFilter, unitFilter, topicFilter, tr]);

  const filterLabelClass = "mb-1.5 block text-sm font-medium text-gray-700";
  const emptyLabel = tr(
    "No hay conocimiento registrado.",
    "No knowledge recorded.",
  );

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden bg-gray-50">
      <div
        className={`shrink-0 border-b border-gray-200 bg-white py-4 lg:px-6 ${MOBILE_PAGE_TITLE_MENU_INSET}`}
      >
        <h1 className="text-2xl font-bold text-gray-900">
          {tr("Conocimiento", "Knowledge")}
        </h1>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-4 max-lg:pl-4 lg:px-6 lg:pb-6">
        <div className="mb-4 shrink-0 grid grid-cols-1 gap-3 md:grid-cols-3">
          <div>
            <label className={filterLabelClass}>
              {tr("Edificio", "Building")}
            </label>
            <Select
              value={condoFilter}
              onChange={handleCondoChange}
              options={condoOptions}
            />
          </div>
          <div>
            <label className={filterLabelClass}>
              {tr("Unidad", "Unit")}
            </label>
            <Select
              value={unitFilter}
              onChange={setUnitFilter}
              options={unitOptions}
              disabled={!condoFilter}
            />
          </div>
          <div>
            <label className={filterLabelClass}>
              {tr("Tema", "Topic")}
            </label>
            <Select
              value={topicFilter}
              onChange={setTopicFilter}
              options={topicOptions}
            />
          </div>
        </div>

        <KnowledgeDataGrid
          items={knowledge}
          emptyLabel={emptyLabel}
          loading={loading}
          error={error}
          tr={tr}
        />
      </div>
    </div>
  );
}
